import React, { createContext, useContext, ReactNode } from 'react';

import { Achievement, AchievementContext } from '../achievements/types';
import type { GalaxyState } from '../components/SpaceCivMode';
import {
  Position,
  PlayerFacing,
  BeltItem,
  Building,
  BiomeConfig,
  ResearchId,
  ItemType,
  BeltType,
} from '../constants';
import type { UpgradeAssemblerResult } from '../GameEngine';
import { QuickBeltPlacementResult, QuickBeltPreviewState } from '../GameEngine';
import {
  AssemblerInventoryLayer,
  RejectedItem,
  ChestInventoryLayer,
  PlayerInventory,
  PowerDemandByBuildingType,
  PowerSnapshot,
  SaveData,
  ResourceNodeMenuState,
  MineResourceResult,
  CollectBuildingInventoryResult,
  ItemProductionHistoryEntry,
} from '../GameState';
import { HandCraftingQueueEntry } from '../HandCrafting';
import { PrestigeUpgradeId, PrestigeUpgradeState } from '../PrestigeSystem';
import { ResearchState } from '../ResearchSystem';
import { RendererMode } from '../types/Renderer';

// Map State interface
interface MapState {
  // World state
  playerPos: Position;
  gameMap: Record<string, string>;
  buildingLayer: Record<string, string>;
  beltContentLayer: Record<string, BeltItem[]>;
  assemblerInventoryLayer: AssemblerInventoryLayer;
  chestInventoryLayer: ChestInventoryLayer;
  rejectedItemsLayer: Record<string, RejectedItem>;
  minerTimerLayer: Record<string, number>;
  minerOutputBufferLayer: Record<string, number>;
  playerInventory: PlayerInventory;

  // Game state
  prestigePoints: number;
  prestigeCount: number;
  cameraPos: Position;
  currentBiome: string;
  playerFacing: PlayerFacing;
  zoomLevel: number;
  tickSpeedMultiplier: number;
  isLoading: boolean;
  hasEnabledDebug: boolean;
  achievements: Achievement[];
  researchState: ResearchState;
  researchMenuOpen: boolean;
  prestigeMenuOpen: boolean;
  prestigeUpgradeState: PrestigeUpgradeState;
  powerProduction: number;
  powerDemand: number;
  powerSatisfaction: number;
  powerHistory: PowerSnapshot[];
  powerDemandByBuildingType: PowerDemandByBuildingType;
  productionMenuOpen: boolean;
  itemProductionHistory: ItemProductionHistoryEntry[];
  currentTick: number;
  handCraftingQueue: HandCraftingQueueEntry[];
  spaceGalaxyState: GalaxyState;
  resourceNodeMenu: ResourceNodeMenuState | null;
  rendererMode: RendererMode;
  quickBeltPreview: QuickBeltPreviewState | null;
}

// Map Actions interface
interface MapActions {
  // World actions
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>,
    buildingLayer?: Record<string, string>
  ) => string | undefined;
  getTargetPosition: (building?: Building) => Position;
  getBuildingTargetLocation: (building: Building) => Position;
  placeBuilding: (x: number, y: number, building: Building) => boolean;
  deconstructBuilding: (x: number, y: number) => boolean;
  upgradeAssemblerToFactory: (position: Position) => UpgradeAssemblerResult;

  // Camera and view actions
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;

  // Game actions
  saveGame: () => boolean;
  loadGameFromSaveData: (saveData: SaveData) => void;
  resetGame: () => void;
  collectResource: (x: number, y: number) => void;
  collectBuildingInventory: (
    position: Position,
    itemType?: ItemType
  ) => CollectBuildingInventoryResult;
  openResourceNodeMenu: (position: Position) => void;
  closeResourceNodeMenu: () => void;
  mineResourceNode: (position: Position) => MineResourceResult;
  addItemToInventory: (itemType: ItemType, amount: number) => number;
  queueHandCrafting: (itemType: ItemType, amount: number) => boolean;
  updateBiome: (biomeName: string) => void;
  movePlayer: (newX: number, newY: number) => void;
  updateTile: (x: number, y: number, character: string) => void;
  launchRocket: () => void;
  forcePrestige: (count: number) => void;
  updatePlayerInventory: (
    updater: (inventory: PlayerInventory) => PlayerInventory
  ) => void;
  setHasEnabledDebug: (enabled: boolean) => void;
  setTickSpeedMultiplier: (multiplier: number) => void;
  cycleTickSpeedMultiplier: () => number;
  getAchievements: () => Achievement[];
  triggerAchievementCheck: (context: AchievementContext) => void;
  unlockResearch: (researchId: ResearchId) => void;
  forceUnlockResearch: (researchId: ResearchId) => void;
  isResearchUnlocked: (researchId: ResearchId) => boolean;
  getUnlockedResearchIds: () => ResearchId[];
  setResearchMenuOpen: (open: boolean) => void;
  setProductionMenuOpen: (open: boolean) => void;
  setPrestigeMenuOpen: (open: boolean) => void;
  selectResearch: (researchId: ResearchId | null) => void;
  selectPrestigeUpgrade: (upgradeId: PrestigeUpgradeId | null) => void;
  purchasePrestigeUpgrade: (upgradeId: PrestigeUpgradeId) => boolean;
  resetResearch: (researchId: ResearchId) => void;
  resetAllResearch: () => void;
  setRendererMode: (mode: RendererMode) => void;
  toggleRendererMode: () => void;
  startQuickBeltPlacement: (beltType: BeltType, position: Position) => void;
  extendQuickBeltPlacement: (position: Position) => void;
  finalizeQuickBeltPreview: () => void;
  cancelQuickBeltPlacement: () => void;
  confirmQuickBeltPlacement: () => QuickBeltPlacementResult;

  // State setters (for advanced usage)
  setAssemblerInventoryLayer: React.Dispatch<
    React.SetStateAction<AssemblerInventoryLayer>
  >;
  setChestInventoryLayer: React.Dispatch<
    React.SetStateAction<ChestInventoryLayer>
  >;
  setBeltContentLayer: React.Dispatch<
    React.SetStateAction<Record<string, BeltItem[]>>
  >;
  setRejectedItemsLayer: React.Dispatch<
    React.SetStateAction<Record<string, RejectedItem>>
  >;
  setMinerTimerLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setMinerOutputBufferLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setBuildingLayer: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setSpaceGalaxyState: React.Dispatch<React.SetStateAction<GalaxyState>>;

  // World generation
  preGenerateArea: (centerX: number, centerY: number, radius?: number) => void;
  getBiome: (x: number, y: number) => BiomeConfig;
}

// Combined context type
type MapContextType = MapState & MapActions;

// Create context
const MapContext = createContext<MapContextType | undefined>(undefined);

// Hook to use Map context
export const useMapState = (): MapContextType => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapState must be used within MapStateManager');
  }
  return context;
};

// MapStateManager component
interface MapStateManagerProps {
  children: ReactNode;
  // Props from useWorld hook
  worldState: MapState;
  worldActions: MapActions;
}

export const MapStateManager: React.FC<MapStateManagerProps> = ({
  children,
  worldState,
  worldActions,
}) => {
  const contextValue: MapContextType = {
    ...worldState,
    ...worldActions,
  };

  return (
    <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
  );
};
